import { buildGoalRecord, type GoalInput, type GoalRecord } from '~/models/goal'
import { toIsoTimestamp } from '~/types/dates'
import type { GoalId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, storageError, toStorageError } from '../errors'
import { notifyWritten } from '../invalidation'
import { STORE } from '../schema'

export async function getGoal(
  db: SolaDatabase,
  id: GoalId,
): Promise<Result<GoalRecord | undefined, StorageError>> {
  try {
    return ok(await db.get(STORE.goals, id))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function getActiveGoal(
  db: SolaDatabase,
): Promise<Result<GoalRecord | undefined, StorageError>> {
  try {
    const active = await db.getAllFromIndex(STORE.goals, 'status', 'active')
    const sorted = [...active].sort((left, right) => right.startDate.localeCompare(left.startDate))
    return ok(sorted[0])
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Edit an existing goal in place.
 *
 * The stored record supplies the identity, so an edit can neither mint a second
 * goal nor change the status of the one it edits. A goal that no longer exists
 * is a typed failure rather than a silent insert — an edit form outliving its
 * record should say so, not quietly create a new goal.
 */
export async function updateGoal(
  db: SolaDatabase,
  id: GoalId,
  input: GoalInput,
  now: Date,
): Promise<Result<GoalRecord, StorageError>> {
  try {
    const existing = await db.get(STORE.goals, id)
    if (existing === undefined) {
      return err(storageError('invalidRecord', 'That goal no longer exists.'))
    }

    const built = buildGoalRecord(input, toIsoTimestamp(now), {
      id: existing.id,
      status: existing.status,
      createdAt: existing.createdAt,
    })
    if (!built.ok) {
      return err(
        storageError(
          'invalidRecord',
          built.error.map((entry) => entry.message).join(' '),
          built.error,
        ),
      )
    }

    await db.put(STORE.goals, built.value)
    notifyWritten(STORE.goals)
    return ok(built.value)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function createGoal(
  db: SolaDatabase,
  input: GoalInput,
  now: Date,
): Promise<Result<GoalRecord, StorageError>> {
  const built = buildGoalRecord(input, toIsoTimestamp(now))
  if (!built.ok) {
    return err(
      storageError(
        'invalidRecord',
        built.error.map((entry) => entry.message).join(' '),
        built.error,
      ),
    )
  }

  try {
    await db.put(STORE.goals, built.value)
    notifyWritten(STORE.goals)
    return ok(built.value)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}
